import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Car, Plus, X, Loader2, Fingerprint, Link2, IdCard } from "lucide-react";
import { PersonProfileCard } from "@/components/PersonProfileCard";
import { VehicleProfileCard } from "@/components/VehicleProfileCard";

const ROLES = ["suspect", "victim", "witness", "complainant", "driver", "passenger", "owner", "other"];
const ROLE_COLOR = { suspect: "#EF4444", victim: "#F59E0B", witness: "#3B82F6", complainant: "#22D3EE", driver: "#10B981", passenger: "#10B981", owner: "#A78BFA", other: "#9CA3AF" };

// Shared panel (dispatch IncidentDetail + MDT): link persons & vehicles to a call.
export const CallEntities = ({ incident, onChanged }) => {
  const nav = useNavigate();
  const [form, setForm] = useState(null); // null | {kind, ...fields}
  const [profile, setProfile] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hot, setHot] = useState({ persons: new Set(), plates: new Set() });
  const ents = incident.entities || [];
  // Hot repeat players: flag entities whose incident volume spiked recently.
  useEffect(() => {
    client.get("/links/hot").then(({ data }) => setHot({
      persons: new Set((data.hot_persons || []).map((p) => p.label.toLowerCase())),
      plates: new Set((data.hot_vehicles || []).map((v) => v.label.replace(/[^A-Z0-9]/g, ""))),
    })).catch(() => {});
  }, []);
  const isHot = (e) => e.kind === "person"
    ? !!e.name && hot.persons.has(e.name.toLowerCase())
    : !!e.plate && hot.plates.has(e.plate.toUpperCase().replace(/[^A-Z0-9]/g, ""));
  const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-[11px] outline-none focus:border-[#3B82F6] font-mono text-white w-full rounded-sm";
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/incidents/${incident.id}/entities`, form);
      if (data.federal_hits?.length) {
        toast.error(`🚨 FEDERAL HIT — "${data.name}" matches ${data.federal_hits.length} federal record(s) [${[...new Set(data.federal_hits.map((h) => h.source))].join(", ")}]. Verify identity before enforcement.`, { duration: 12000 });
      } else {
        toast.success(`${form.kind === "person" ? "Person" : "Vehicle"} linked to call`);
      }
      setForm(null); onChanged?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Link failed");
    } finally { setBusy(false); }
  };
  const remove = async (eid) => {
    try { await client.delete(`/incidents/${incident.id}/entities/${eid}`); onChanged?.(); }
    catch { toast.error("Unlink failed"); }
  };
  const [ncicBusy, setNcicBusy] = useState(null); // eid being queried
  const [ncicRes, setNcicRes] = useState({}); // eid -> summary
  const runNcic = async (e) => {
    setNcicBusy(e.eid);
    try {
      const { data } = await client.post(`/incidents/${incident.id}/entities/${e.eid}/quick-ncic`);
      setNcicRes((p) => ({ ...p, [e.eid]: data }));
      if (data.count > 0) {
        toast.error(`🚨 NCIC — '${data.value}': ${data.summary}. CJI — verify before enforcement.`, { duration: 10000 });
      } else {
        toast.success(`NCIC — '${data.value}': NO RECORD (purpose C, logged)`);
      }
      onChanged?.();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "NCIC query failed");
    } finally { setNcicBusy(null); }
  };

  return (
    <div data-testid="call-entities" className="border border-[#2C2D35] bg-[#141518] p-3 rounded-sm">
      <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-2 flex items-center gap-2">
        Persons &amp; Vehicles Linked <span className="text-[#6B7280]">({ents.length})</span>
        <div className="ml-auto flex gap-1.5">
          <button data-testid="entity-add-person" onClick={() => setForm({ kind: "person", role: "suspect", name: "", dob: "", sex: "", description: "", notes: "" })}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-head font-bold uppercase bg-[#3B82F6]/15 border border-[#3B82F6]/50 text-[#60A5FA] hover:bg-[#3B82F6]/25 transition-colors rounded-sm"><Plus size={11} /> Person</button>
          <button data-testid="entity-add-vehicle" onClick={() => setForm({ kind: "vehicle", role: "driver", plate: "", state: "", make: "", model: "", color: "", year: "", notes: "" })}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-head font-bold uppercase bg-[#10B981]/15 border border-[#10B981]/50 text-[#4ade80] hover:bg-[#10B981]/25 transition-colors rounded-sm"><Plus size={11} /> Vehicle</button>
        </div>
      </div>

      {form && (
        <div data-testid="entity-form" className="border border-[#2C2D35] bg-[#0d0e11] p-2.5 mb-2 rounded-sm">
          <div className="grid grid-cols-2 gap-1.5">
            <select data-testid="entity-role" value={form.role} onChange={set("role")} className={inp}>
              {ROLES.map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
            </select>
            {form.kind === "person" ? (
              <>
                <input data-testid="entity-name" value={form.name} onChange={set("name")} placeholder="Full name *" className={inp} />
                <input data-testid="entity-dob" value={form.dob} onChange={set("dob")} placeholder="DOB (YYYY-MM-DD)" className={inp} />
                <select value={form.sex} onChange={set("sex")} className={inp}>
                  <option value="">Sex —</option><option value="M">M</option><option value="F">F</option><option value="X">X</option>
                </select>
                <input value={form.description} onChange={set("description")} placeholder="Physical description / clothing" className={`${inp} col-span-2`} />
              </>
            ) : (
              <>
                <input data-testid="entity-plate" value={form.plate} onChange={set("plate")} placeholder="Plate *" className={inp} />
                <input value={form.state} onChange={set("state")} placeholder="State (PA/MD…)" className={inp} />
                <input value={form.year} onChange={set("year")} placeholder="Year" className={inp} />
                <input value={form.color} onChange={set("color")} placeholder="Color" className={inp} />
                <input data-testid="entity-make" value={form.make} onChange={set("make")} placeholder="Make" className={inp} />
                <input value={form.model} onChange={set("model")} placeholder="Model" className={inp} />
              </>
            )}
            <input value={form.notes} onChange={set("notes")} placeholder="Notes (optional)" className={`${inp} col-span-2`} />
          </div>
          <div className="flex gap-1.5 mt-2">
            <button data-testid="entity-save" onClick={save} disabled={busy}
              className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-head font-bold text-black rounded-sm" style={{ background: "#10B981" }}>
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} LINK TO CALL
            </button>
            <button onClick={() => setForm(null)} className="px-3 py-1.5 text-[10px] font-head border border-[#2C2D35] text-[#9CA3AF] rounded-sm">CANCEL</button>
          </div>
        </div>
      )}

      {ents.length === 0 && !form && (
        <div className="font-mono text-[11px] text-[#6B7280]">No persons or vehicles linked. Use + Person / + Vehicle to attach subjects, victims, witnesses or vehicles to this call.</div>
      )}
      <div className="space-y-1.5">
        {ents.map((e) => (
          <div key={e.eid} data-testid={`entity-row-${e.eid}`} className="flex items-center gap-2 px-2.5 py-2 bg-[#0d0e11] border-l-2 rounded-sm"
            style={{ borderColor: ROLE_COLOR[e.role] || "#9CA3AF" }}>
            {e.kind === "person" ? <User size={14} className="text-[#60A5FA] shrink-0" /> : <Car size={14} className="text-[#4ade80] shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="text-xs text-white font-semibold truncate">
                {e.kind === "person"
                  ? <>{e.name}{e.dob ? ` · DOB ${e.dob}` : ""}{e.sex ? ` · ${e.sex}` : ""}</>
                  : <>{[e.year, e.color, e.make, e.model].filter(Boolean).join(" ") || "Vehicle"}{e.plate ? ` · ${e.plate}${e.state ? `/${e.state}` : ""}` : ""}</>}
              </div>
              <div className="font-mono text-[9px] text-[#6B7280] truncate">
                {e.description || e.notes || ""}{(e.description || e.notes) ? " · " : ""}added by {e.added_by}
              </div>
              {isHot(e) && (
                <div data-testid={`entity-hot-${e.eid}`} className="font-mono text-[9px] font-bold text-white bg-[#EA580C] px-1.5 py-0.5 mt-1 inline-block rounded-sm animate-pulse"
                  title="HOT — repeat player: 3+ linked incidents in the last 30 days. Tap Links for the full history.">
                  🔥 HOT — REPEAT PLAYER
                </div>
              )}
              {e.watchlist?.length > 0 && (
                <div data-testid={`entity-watch-${e.eid}`} className="font-mono text-[9px] font-bold text-white bg-[#B91C1C] px-1.5 py-0.5 mt-1 inline-block rounded-sm animate-pulse border border-[#FCA5A5]"
                  title={e.watchlist.map((w) => `${w.reason}${w.note ? ` — ${w.note}` : ""} (flagged by ${w.by})`).join("\n")}>
                  🚩 WATCHLIST PLATE — {e.watchlist.map((w) => w.reason).join(" · ")} · dispatch alerted
                </div>
              )}
              {e.safety_flags?.length > 0 && (
                <div data-testid={`entity-safety-${e.eid}`} className="font-mono text-[9px] font-bold text-white bg-[#B91C1C] px-1.5 py-0.5 mt-1 inline-block rounded-sm animate-pulse border border-[#FCA5A5]"
                  title={e.safety_flags.map((f) => `${f.flag}${f.note ? ` — ${f.note}` : ""} (pinned by ${f.by})`).join("\n")}>
                  ⚠ OFFICER SAFETY — {e.safety_flags.map((f) => f.flag).join(" · ")}
                </div>
              )}
              {e.federal_hits?.length > 0 && (
                <div data-testid={`entity-federal-hit-${e.eid}`} className="font-mono text-[9px] font-bold text-white bg-[#DC2626] px-1.5 py-0.5 mt-1 inline-block rounded-sm animate-pulse"
                  title={e.federal_hits.map((h) => `${h.source}: ${h.name}`).join(" | ")}>
                  🚨 FEDERAL HIT ×{e.federal_hits.length} — {e.federal_hits[0].name}{e.federal_hits.length > 1 ? " +" : ""} · VERIFY ID
                </div>
              )}
              {ncicRes[e.eid] && (
                <div data-testid={`entity-ncic-result-${e.eid}`} className={`font-mono text-[9px] font-bold px-1.5 py-0.5 mt-1 inline-block rounded-sm border ${ncicRes[e.eid].count > 0 ? "text-[#DC2626] border-[#DC2626]/60 bg-[#DC2626]/10" : "text-[#22C55E] border-[#22C55E]/50 bg-[#22C55E]/10"}`}>
                  NCIC: {ncicRes[e.eid].summary}
                </div>
              )}
            </div>
            <button data-testid={`entity-links-${e.eid}`}
              onClick={() => nav(`/links?q=${encodeURIComponent(e.kind === "person" ? e.name : e.plate)}`)}
              disabled={e.kind === "person" ? !e.name : !e.plate}
              title="View all incidents, addresses & associates linked to this person/plate"
              className="flex items-center gap-1 px-1.5 py-1 text-[9px] font-head font-bold uppercase bg-[#22D3EE]/15 border border-[#22D3EE]/50 text-[#67E8F9] hover:bg-[#22D3EE]/25 transition-colors rounded-sm shrink-0 disabled:opacity-40">
              <Link2 size={11} /> Links
            </button>
            {e.kind === "vehicle" && (
              <button data-testid={`entity-vehicle-profile-${e.eid}`} onClick={() => setVehicle(e.plate)} disabled={!e.plate}
                title="Full plate history — stops, calls, owners/occupants, plate runs, tows"
                className="flex items-center gap-1 px-1.5 py-1 text-[9px] font-head font-bold uppercase bg-[#10B981]/15 border border-[#10B981]/50 text-[#6EE7B7] hover:bg-[#10B981]/25 transition-colors rounded-sm shrink-0 disabled:opacity-40">
                <IdCard size={11} /> Profile
              </button>
            )}
            {e.kind === "person" && (
              <button data-testid={`entity-profile-${e.eid}`} onClick={() => setProfile({ name: e.name, dob: e.dob })} disabled={!e.name}
                title="Full contact history — every call, warrant, form and record for this person"
                className="flex items-center gap-1 px-1.5 py-1 text-[9px] font-head font-bold uppercase bg-[#3B82F6]/15 border border-[#3B82F6]/50 text-[#93C5FD] hover:bg-[#3B82F6]/25 transition-colors rounded-sm shrink-0 disabled:opacity-40">
                <IdCard size={11} /> Profile
              </button>
            )}
            <button data-testid={`entity-ncic-${e.eid}`} onClick={() => runNcic(e)} disabled={ncicBusy === e.eid}
              title="One-tap NCIC wants/warrants check (purpose C · CJIS-logged)"
              className="flex items-center gap-1 px-1.5 py-1 text-[9px] font-head font-bold uppercase bg-[#8B5CF6]/15 border border-[#8B5CF6]/50 text-[#C4B5FD] hover:bg-[#8B5CF6]/25 transition-colors rounded-sm shrink-0 disabled:opacity-50">
              {ncicBusy === e.eid ? <Loader2 size={11} className="animate-spin" /> : <Fingerprint size={11} />} Run NCIC
            </button>
            <span className="font-mono text-[9px] font-bold uppercase px-1.5 py-0.5 border rounded-sm shrink-0"
              style={{ color: ROLE_COLOR[e.role] || "#9CA3AF", borderColor: `${ROLE_COLOR[e.role] || "#9CA3AF"}55` }}>{e.role || "linked"}</span>
            <button data-testid={`entity-remove-${e.eid}`} onClick={() => remove(e.eid)} className="text-[#6B7280] hover:text-[#EF4444] shrink-0" aria-label="Unlink"><X size={13} /></button>
          </div>
        ))}
      </div>
      <PersonProfileCard name={profile?.name} dob={profile?.dob} open={!!profile} onClose={() => setProfile(null)} />
      <VehicleProfileCard plate={vehicle} open={!!vehicle} onClose={() => setVehicle(null)} onOpenPerson={(n) => { setVehicle(null); setProfile({ name: n }); }} />
    </div>
  );
};
